import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, ApiClientError } from "./api.js";
import type { User } from "./types.js";
import { clearAllGenerationDrafts } from "./generation-draft.js";

interface AuthState {
  status: "loading" | "authenticated" | "anonymous" | "unavailable";
  retrySession(): void;
  user: User | null;
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState["status"]>("loading");
  const [user, setUser] = useState<User | null>(null);
  const [sessionCheck, setSessionCheck] = useState(0);
  useEffect(() => {
    let active = true;
    void api
      .session()
      .then((sessionUser) => {
        if (active) {
          setUser(sessionUser);
          setStatus("authenticated");
        }
      })
      .catch((error: unknown) => {
        if (active) {
          setStatus(
            error instanceof ApiClientError && error.status === 401
              ? "anonymous"
              : "unavailable",
          );
        }
      });
    return () => {
      active = false;
    };
  }, [sessionCheck]);
  const value = useMemo<AuthState>(
    () => ({
      status,
      user,
      retrySession() {
        setStatus("loading");
        setSessionCheck((attempt) => attempt + 1);
      },
      async login(email, password) {
        const authenticated = await api.login(email, password);
        setUser(authenticated);
        setStatus("authenticated");
      },
      async logout() {
        await api.logout();
        clearAllGenerationDrafts();
        setUser(null);
        setStatus("anonymous");
      },
    }),
    [status, user],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error("AUTH_PROVIDER_REQUIRED");
  return value;
}
